"use client";

import * as React from "react";
import { CSS } from "@dnd-kit/utilities";
import { useSortable } from "@dnd-kit/sortable";
import { ChevronDown } from "lucide-react";
import { SwipeableItem } from "@/components/ui/SwipeableItem";
import { TrashCanSVG, ReorderSVG, SelectSVG } from "@/components/ui/SVG";
import RichTextInput from "@/components/ui/RichTextInput";
import {
  cn,
  createEmptyRichText,
  extractPlainTextFromRich,
  getBlockLocaleStrings,
} from "../utils";

export function FaqSortableItem({
  id,
  item,
  index,
  isDesktop,
  inputDir,
  localeStrings,
  isOpen,
  onToggle,
  onRemove,
  onOpenImagePicker,
  onClearImage,
  onUpdateItem,
}: {
  id: string;
  item: any;
  index: number;
  isDesktop: boolean;
  inputDir: string;
  localeStrings: ReturnType<typeof getBlockLocaleStrings>;
  isOpen: boolean;
  onToggle: () => void;
  onRemove: () => void;
  onOpenImagePicker: () => void;
  onClearImage: () => void;
  onUpdateItem: (patch: (item: any) => any) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  const questionText =
    extractPlainTextFromRich(item.question?.richText) ||
    item.question?.text ||
    localeStrings.helpers.question + " " + (index + 1).toString();
  const imageData = item.image ?? {};
  const previewSrc = imageData.previewUrl ?? imageData.url;
  const alignment = inputDir === "rtl" ? "text-right" : "text-left";
  const imagePosition =
    item.imagePosition ?? (inputDir === "rtl" ? "inlineLeft" : "inlineRight");

  return (
    <SwipeableItem
      className="w-full"
      group="faq-items"
      disabled={isDragging || isOpen || isDesktop}
      rightActions={
        isDesktop ? undefined : (
          <button
            type="button"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onRemove();
            }}
            className="mx-6 flex h-full items-center rounded-sm bg-red-600 p-2 text-xs font-semibold tracking-wide text-white uppercase"
          >
            <TrashCanSVG
              className="h-5 w-5"
              svgColor="white"
              accentColor="white"
            />
          </button>
        )
      }
    >
      <div
        ref={setNodeRef}
        style={style}
        className={cn(
          "flex flex-col gap-3 rounded bg-white p-3",
          isDragging && "border-pinkCustom shadow-sm",
        )}
        dir={inputDir as "ltr" | "rtl"}
      >
        <div className="flex items-center justify-between gap-2">
          <p className="line-clamp-2 text-sm font-semibold text-gray-800">
            {questionText}
          </p>

          <div className="flex items-center gap-1">
            <button
              type="button"
              className={cn(
                "cursor-grab rounded-md py-2 text-gray-500 transition hover:bg-gray-50 active:cursor-grabbing",
                isDragging && "text-pinkCustom",
              )}
              style={{ touchAction: "none" }}
              onPointerDown={(event) => event.stopPropagation()}
              onClick={(event) => event.stopPropagation()}
              {...attributes}
              {...listeners}
              aria-label={localeStrings.helpers.dragToReorder}
            >
              <ReorderSVG className="h-4 w-4" />
            </button>
            <button
              type="button"
              className="rounded-md py-2 text-red-600 transition hover:bg-red-50"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onRemove();
              }}
            >
              <TrashCanSVG className="h-4 w-4" svgColor="var(--color-red-600)" accentColor="var(--color-red-600)" />
            </button>
          </div>
        </div>
        <div className="space-y-3">
          <div className="flex items-start gap-2 rounded border border-gray-200">
            <div className="flex-1">
              <RichTextInput
                value={item.question?.richText ?? createEmptyRichText()}
                placeholder={localeStrings.helpers.question}
                onChange={({ json }) =>
                  onUpdateItem((existing) => ({
                    ...existing,
                    question: { ...(existing.question ?? {}), richText: json },
                  }))
                }
                dir={inputDir as "ltr" | "rtl"}
                editorClassName={alignment}
              />
            </div>
            <button
              type="button"
              onClick={onToggle}
              aria-label={
                isOpen
                  ? localeStrings.helpers.collapse
                  : localeStrings.helpers.expand
              }
              aria-expanded={isOpen}
              className="my-auto p-3 text-gray-600 transition hover:bg-gray-50"
            >
              <ChevronDown
                className={cn(
                  "h-5 w-5 transition-transform text-pinkCustom",
                  isOpen ? "rotate-180" : "rotate-0",
                )}
              />
            </button>
          </div>
          {isOpen && (
            <>
              <div className="space-y-1">
                <RichTextInput
                  value={item.answer?.richText ?? createEmptyRichText()}
                  placeholder={localeStrings.helpers.answer}
                  onChange={({ json }) =>
                    onUpdateItem((existing) => ({
                      ...existing,
                      answer: { ...(existing.answer ?? {}), richText: json },
                    }))
                  }
                  dir={inputDir as "ltr" | "rtl"}
                  editorClassName={alignment}
                  className="border-gray-200 rounded border"
                />
              </div>
              <div className="space-y-2">
                <label className="flex items-center justify-between gap-2 text-xs font-medium text-gray-700">
                  <span>{localeStrings.helpers.imagePosition}</span>
                  <select
                    className="rounded border border-gray-300 bg-white px-2 py-1 text-xs"
                    value={imagePosition}
                    onChange={(event) =>
                      onUpdateItem((existing) => ({
                        ...existing,
                        imagePosition: event.target.value,
                      }))
                    }
                  >
                    <option value="inlineLeft">
                      {localeStrings.helpers.imagePosInlineLeft}
                    </option>
                    <option value="inlineRight">
                      {localeStrings.helpers.imagePosInlineRight}
                    </option>
                    <option value="top">
                      {localeStrings.helpers.imagePosTop}
                    </option>
                    <option value="bottom">
                      {localeStrings.helpers.imagePosBottom}
                    </option>
                  </select>
                </label>
                <div className="flex items-center justify-between">
                  {previewSrc ? (
                    <button
                      type="button"
                      className="text-xs text-red-600 hover:opacity-80"
                      onClick={onClearImage}
                    >
                      {localeStrings.helpers.clearImg}
                    </button>
                  ) : null}
                </div>
                {previewSrc ? (
                  <div className="overflow-hidden rounded border border-gray-200 bg-white">
                    <div className="flex items-center justify-center bg-gray-100">
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img
                        src={previewSrc}
                        alt=""
                        className="max-h-40 w-full object-contain"
                        loading="lazy"
                      />
                    </div>
                    <div className="flex flex-wrap items-center gap-3 px-3 py-2 text-xs text-gray-600">
                      <div className="min-w-0">
                        {imageData.sourceName ? (
                          <div className="truncate font-medium">
                            {imageData.sourceName}
                          </div>
                        ) : (
                          <div className="font-medium text-gray-500">
                            {localeStrings.helpers.selectedImage}
                          </div>
                        )}
                      </div>
                      <div className="ml-auto flex gap-2">
                        <button
                          type="button"
                          onClick={onOpenImagePicker}
                          className="rounded border border-gray-300 px-2 py-1 text-[11px] font-medium tracking-wide text-gray-700 uppercase hover:bg-gray-100"
                        >
                          {localeStrings.helpers.changeImage ??
                            localeStrings.helpers.change}
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={onOpenImagePicker}
                    className="flex w-full flex-col items-center justify-center gap-2 rounded border border-gray-200 bg-white px-4 py-8 text-sm font-medium text-gray-400 transition"
                  >
                    <SelectSVG className="w-8 rounded bg-slateCustom" svgColor="white" accentColor="white" />
                    <span>{localeStrings.helpers.selectImage}</span>
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </SwipeableItem>
  );
}
